import { useEffect, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Clock, Ellipsis, Hourglass, Pause, Play, CirclePlay, Trash2 } from "lucide-react";
import {
  DeliverTargets, cronIsPaused, describeSchedule, triggerCronJob,
  type CronDeliveryTarget, type CronJob, type CronRun,
} from "@timibot/core";
import { useApp } from "../../context";
import type { AppModel } from "../../state/app";
import { Dialog, IconBadge, Menu, PlainRow, PrimaryButton, SectionTitle, Sheet, Spinner, useLongPress, useMenu, type MenuItem } from "../../ui/components";
import "./workspace.css";

export function cronName(j: CronJob): string {
  return j.name || (j.prompt_preview ? [...j.prompt_preview].slice(0, 30).join("") : j.job_id);
}

/** 在运行的排前面（按下次运行时间），暂停的放后面 */
export function cronSorted(jobs: CronJob[]): CronJob[] {
  return jobs.map((j, i) => [j, i] as const).sort(([a, ai], [b, bi]) => {
    const pa = cronIsPaused(a), pb = cronIsPaused(b);
    if (pa !== pb) return pa ? 1 : -1;
    if (!pa && a.next_run_at && b.next_run_at && a.next_run_at !== b.next_run_at) return a.next_run_at < b.next_run_at ? -1 : 1;
    return ai - bi;
  }).map(([j]) => j);
}

function parseDate(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** “3 小时后”“昨天” */
export function relative(iso?: string): string {
  const d = parseDate(iso);
  if (!d) return iso ?? "";
  const diff = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat("zh-CN", { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

/** 任务跑完了，只是投递到某个外部渠道失败 */
function undelivered(j: CronJob): boolean {
  return !j.last_error && (j.last_status === "delivery_failed" || !!j.last_delivery_error);
}

function undeliveredText(j: CronJob): string {
  const external = DeliverTargets.platforms(j.deliver ?? "").filter((p) => p !== "local" && p !== "origin");
  return external.length === 1 ? `${DeliverTargets.label(external[0])}未送达` : "部分渠道未送达";
}

/** 上次运行的状态。任务本身失败是红色；任务成功了、只是某个渠道没送达是橙色 */
function badge(j: CronJob): { text: string; color: string } | undefined {
  if (j.last_error) return { text: "上次失败", color: "var(--danger)" };
  if (undelivered(j)) return { text: undeliveredText(j), color: "var(--warning)" };
  const status = j.last_status ?? "";
  if (!status || ["ok", "success", "completed"].includes(status)) return undefined;
  return { text: "上次失败", color: "var(--danger)" };
}

/** 暂停期间错过了运行时间：Hermes 恢复时会按补跑规则马上跑一次 */
export function willCatchUp(j: CronJob): boolean {
  const next = parseDate(j.next_run_at);
  return cronIsPaused(j) && !!next && next.getTime() < Date.now();
}

export function catchUpMessage(j: CronJob): string {
  return `“${cronName(j)}”暂停期间错过了运行时间，恢复后 Hermes 会马上补跑一次，结果投递到${DeliverTargets.label(j.deliver ?? "local")}。`;
}

/** 定时任务的暂停 / 恢复 / 立即运行 / 删除 */
export const CronActions = {
  async toggle(job: CronJob, app: AppModel): Promise<boolean> {
    const paused = cronIsPaused(job);
    return manage(paused ? "resume" : "pause", job, app, paused ? "已恢复，会按时运行" : "已暂停");
  },
  async remove(job: CronJob, app: AppModel): Promise<boolean> {
    return manage("remove", job, app, "已删除");
  },
  async runNow(job: CronJob, app: AppModel): Promise<void> {
    if (!app.api) return;
    try {
      await triggerCronJob(app.api, job.job_id, app.selectedProfile);
      app.showToast("已开始运行，完成后会通知你");
      app.bumpCronRevision();
    } catch (e) {
      app.showToast(`没能运行：${e instanceof Error ? e.message : String(e)}`);
    }
  },
};

async function manage(action: string, job: CronJob, app: AppModel, done: string): Promise<boolean> {
  if (!app.api) return false;
  try {
    const r = await app.api.manageCronJob(action, job.job_id, app.selectedProfile);
    if (r.success === false) { app.showToast(r.error ?? "操作失败"); return false; }
    app.showToast(done);
    app.bumpCronRevision();
    return true;
  } catch (e) {
    app.showToast(e instanceof Error ? e.message : String(e));
    return false;
  }
}

function menuItems(job: CronJob, onToggle?: () => void, onRunNow?: () => void, onDelete?: () => void): MenuItem[] {
  const paused = cronIsPaused(job);
  return [
    ...(onRunNow ? [{ label: "立即运行", icon: <CirclePlay size={16} />, onSelect: onRunNow }] : []),
    ...(onToggle ? [{ label: paused ? "恢复" : "暂停", icon: paused ? <Play size={16} /> : <Pause size={16} />, onSelect: onToggle }] : []),
    ...(onDelete ? [{ label: "删除", icon: <Trash2 size={16} />, destructive: true, onSelect: onDelete }] : []),
  ];
}

/** 自动化列表的一行：左边图标点了暂停/恢复，中间看运行记录，右边“⋯”更多 */
export function CronRow({ job, onToggle, onOpen, onRunNow, onDelete }: {
  job: CronJob; onToggle?: () => void; onOpen?: () => void; onRunNow?: () => void; onDelete?: () => void;
}) {
  const paused = cronIsPaused(job);
  const menu = useMenu();
  const press = useLongPress((pos) => { if (onDelete) menu.show(pos); });
  const when = describeSchedule(job.schedule);
  const subtitle = paused ? `已暂停 · ${when}` : job.next_run_at ? `${when} · ${relative(job.next_run_at)}` : when;
  const b = badge(job);
  return (
    <div className="cron-row" {...press}>
      <button className="cron-toggle" onClick={onToggle} disabled={!onToggle} aria-label={paused ? "恢复" : "暂停"}>
        <IconBadge icon={paused ? <Pause size={15} /> : <Clock size={15} />} size={34} dimmed={paused} />
      </button>
      <button className="cron-main" onClick={onOpen}>
        <span className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <span className={`t-subheadline medium nowrap${paused ? " t-2" : ""}`}>{cronName(job)}</span>
          <span className="t-caption t-2 nowrap">{subtitle}</span>
        </span>
        {b && <span className="t-caption2 medium nowrap" style={{ color: b.color }}>{b.text}</span>}
      </button>
      {onDelete && (
        <button className="cron-more" aria-label="更多操作" onClick={(e) => menu.show(e.currentTarget)}><Ellipsis size={18} /></button>
      )}
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="end" width={170} items={menuItems(job, onToggle, onRunNow, onDelete)} />
    </div>
  );
}

const presets: [string, string][] = [
  ["每天 9:00", "0 9 * * *"], ["每天 21:00", "0 21 * * *"], ["工作日 9:00", "0 9 * * 1-5"],
  ["每小时", "every 1h"], ["每 30 分钟", "every 30m"], ["每周一 10:00", "0 10 * * 1"],
];

export function CreateCronSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => Promise<void> }) {
  const app = useApp();
  const [name, setName] = useState("");
  const [schedule, setSchedule] = useState("0 9 * * *");
  const [prompt, setPrompt] = useState("");
  const [deliver, setDeliver] = useState("local");
  const [targets, setTargets] = useState<CronDeliveryTarget[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setName(""); setSchedule("0 9 * * *"); setPrompt(""); setDeliver("local"); setError(undefined);
    app.api?.cronDeliveryTargets(app.selectedProfile).then(setTargets, () => setTargets([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!app.api) return;
    setBusy(true);
    try {
      const r = await app.api.addCronJob(name || undefined, schedule, prompt, deliver || undefined, app.selectedProfile);
      if (r.success === false) { setError(r.error ?? "创建失败"); return; }
      await onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="新建定时任务" leading={<button className="sheet-done" onClick={onClose}>取消</button>}
      trailing={<button className="sheet-done semibold" disabled={busy || !prompt.trim() || !schedule} onClick={() => void create()}>{busy ? "创建中…" : "创建"}</button>}>
      <div className="form">
        <label className="form-group">
          <span className="form-label">名称</span>
          <input className="form-field" placeholder="例如：早间简报" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="form-group">
          <span className="form-label">时间</span>
          <input className="form-field mono" placeholder="cron 表达式或 every 1h" value={schedule} autoCapitalize="none" autoCorrect="off" onChange={(e) => setSchedule(e.target.value)} />
          <span className="t-caption t-2 form-hint">{describeSchedule(schedule)}</span>
          <div className="chip-row">
            {presets.map(([title, value]) => <button key={value} className={`chip${schedule === value ? " on" : ""}`} onClick={() => setSchedule(value)}>{title}</button>)}
          </div>
        </div>
        <label className="form-group">
          <span className="form-label">要做什么</span>
          <textarea className="form-field" rows={6} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="例如：整理今天值得关注的科技新闻，每条一句话" />
        </label>
        <div className="form-group">
          <span className="form-label">结果投递到</span>
          <div className="form-card">
            {DeliverTargets.options(targets, deliver).map((t) => (
              <DeliverOption key={t.id} title={t.title} unavailable={t.unavailable} checked={DeliverTargets.contains(deliver, t.id)} onToggle={() => setDeliver(DeliverTargets.toggle(deliver, t.id))} />
            ))}
          </div>
        </div>
        {error && <div className="t-footnote t-danger">{error}</div>}
      </div>
    </Sheet>
  );
}

function DeliverOption({ title, unavailable, checked, onToggle }: { title: string; unavailable?: boolean; checked: boolean; onToggle: () => void }) {
  return (
    <button className="deliver-option" onClick={onToggle}>
      <span className="col" style={{ flex: 1, gap: 2 }}>
        <span>{title}</span>
        {unavailable && <span className="t-caption t-2">还没有设置主频道</span>}
      </span>
      {checked && <Check size={17} strokeWidth={2.6} className="t-brand" />}
    </button>
  );
}

/** 一个定时任务的运行记录。每次运行都是一个会话，点开就能看完整过程和结果。 */
export function CronRunsSheet({ job, onClose }: { job?: CronJob; onClose: () => void }) {
  const app = useApp();
  const [paused, setPaused] = useState<boolean>();
  const [runs, setRuns] = useState<CronRun[]>();
  const [error, setError] = useState<string>();
  const [fullPrompt, setFullPrompt] = useState<string>();
  const [expanded, setExpanded] = useState(false);
  const [deliver, setDeliver] = useState<string>();
  const [targets, setTargets] = useState<CronDeliveryTarget[]>([]);
  const [page, setPage] = useState<"main" | "deliver">("main");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menu = useMenu();

  const loadRuns = async (j: CronJob) => {
    try { setRuns(await app.api?.cronRuns(j.job_id, app.selectedProfile) ?? []); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  useEffect(() => {
    if (!job) return;
    setPaused(undefined); setRuns(undefined); setError(undefined); setFullPrompt(undefined); setExpanded(false);
    setDeliver(undefined); setPage("main");
    void loadRuns(job);
    // 访客没有这两个接口的权限：用列表里的预览
    if (!app.isGuest && app.api) {
      app.api.cronJobDetail(job.job_id, app.selectedProfile).then((d) => { setFullPrompt(d.prompt); setDeliver(d.deliver); }, () => {});
      app.api.cronDeliveryTargets(app.selectedProfile).then(setTargets, () => {});
    }
  }, [job?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!job) return <Sheet open={false} onClose={onClose}>{null}</Sheet>;
  const isPaused = paused ?? cronIsPaused(job);
  const current: CronJob = { ...job, enabled: !isPaused, state: isPaused ? "paused" : "scheduled" };
  const prompt = fullPrompt ?? job.prompt_preview ?? "";
  const isLong = prompt.length > 110 || (prompt.match(/\n/g)?.length ?? 0) >= 4;
  const currentDeliver = deliver ?? job.deliver ?? "local";

  const saveDeliver = async (value: string) => {
    const previous = currentDeliver;
    setDeliver(value);
    try {
      await app.api?.updateCronJob(job.job_id, { deliver: value }, app.selectedProfile);
      app.bumpCronRevision();
    } catch (e) {
      setDeliver(previous);
      app.showToast(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={page === "deliver" ? "投递到" : cronName(job)}
      leading={page === "deliver"
        ? <button className="sheet-done row" onClick={() => setPage("main")}><ChevronLeft size={18} />返回</button>
        : <button className="sheet-done" onClick={onClose}>关闭</button>}
      trailing={page === "main" && !app.isGuest ? <button className="glass-button" style={{ width: 38, height: 38 }} aria-label="更多操作" onClick={(e) => menu.show(e.currentTarget)}><Ellipsis size={18} /></button> : undefined}
    >
      {page === "main" ? (
        <div className="runs">
          <div className="runs-summary">
            <span className="t-caption t-2">{describeSchedule(job.schedule)}{isPaused ? " · 已暂停" : ""}</span>
            {prompt && <p className={`t-subheadline runs-prompt${expanded ? "" : " collapsed"}`}>{prompt}</p>}
            {isLong && <button className="t-caption medium t-2" onClick={() => setExpanded((v) => !v)}>{expanded ? "收起" : "展开全部"}</button>}
            {job.last_error ? <span className="t-caption t-danger clamp2">上次出错：{job.last_error}</span>
              : undelivered(job) ? <span className="t-caption clamp2" style={{ color: "var(--warning)" }}>上次任务完成了，结果在 TimiBot 里，但{undeliveredText(job)}{job.last_delivery_error ? `（${job.last_delivery_error}）` : ""}</span> : null}
          </div>
          {!app.isGuest && (
            <button className="value-row" onClick={() => setPage("deliver")}>
              <span className="t-subheadline">投递到</span>
              <span className="spacer" />
              <span className="t-subheadline t-2">{DeliverTargets.label(currentDeliver)}</span>
              <ChevronRight size={14} className="t-3" />
            </button>
          )}
          <SectionTitle title="运行记录" />
          {error && <div className="t-footnote t-danger ws-pad">{error}</div>}
          {runs && runs.length === 0 && <div className="t-footnote t-2 ws-pad">{job.next_run_at ? `还没有运行过，下次：${relative(job.next_run_at)}` : "还没有运行过"}</div>}
          {!runs && !error && <div className="center-pad"><Spinner /></div>}
          {(runs ?? []).map((run) => (
            <PlainRow
              key={run.id}
              icon={run.is_active ? <Hourglass size={15} /> : <Check size={15} />}
              title={run.started_at ? new Date(run.started_at * 1000).toLocaleString("zh-CN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : run.id}
              subtitle={run.is_active ? "正在运行…" : run.tool_call_count === undefined ? undefined : run.tool_call_count === 0 ? "直接给出了结果" : `调用工具 ${run.tool_call_count} 次`}
              trailing={run.is_active ? <Spinner size={14} /> : <ChevronRight size={14} className="t-3" />}
              onClick={() => { onClose(); app.openStored(run.id, app.selectedProfile, run.title); }}
            />
          ))}
        </div>
      ) : (
        <div className="form">
          <div className="form-card">
            {DeliverTargets.options(targets, currentDeliver).map((t) => (
              <DeliverOption key={t.id} title={t.title} unavailable={t.unavailable} checked={DeliverTargets.contains(currentDeliver, t.id)}
                onToggle={() => void saveDeliver(DeliverTargets.toggle(currentDeliver, t.id))} />
            ))}
          </div>
        </div>
      )}
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="end" width={170} items={menuItems(current,
        () => void CronActions.toggle(current, app).then((ok) => { if (ok) setPaused(!isPaused); }),
        () => void CronActions.runNow(job, app).then(() => loadRuns(job)),
        () => setConfirmDelete(true))} />
      <Dialog open={confirmDelete} title={`删除“${cronName(job)}”？`} message="任务会从服务器上删掉，删除后不能恢复。" onClose={() => setConfirmDelete(false)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "删除", role: "destructive", onClick: () => void CronActions.remove(job, app).then((ok) => { if (ok) onClose(); }) }]} />
    </Sheet>
  );
}

export { PrimaryButton };
